/**
 * Settings → Sjablonen and → Plugins: make a new one, or open one, without
 * leaving the settings first. The full overviews stay where they were.
 */
import { useTranslation } from 'react-i18next';
import { FileCode2, LayoutTemplate, Plus, Puzzle, Sparkles } from 'lucide-react';
import { createGeneralTemplate, listAllTemplates } from '@nexus/workspace';
import { useStore, useStoreQuery } from '../db/context';
import { useUi } from '../state/ui';
import { useTemplateUi } from '../templates/state';
import { usePluginEditor } from '../plugins/editorState';
import { useBuilder } from '../plugins/builderState';
import { usePlugins } from '../plugins/runtime';
import { PageIcon } from './PageIcon';

const primary =
  'flex items-center gap-1.5 rounded-full bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg';
const secondary =
  'flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-sm hover:bg-bg-hover';
const row =
  'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-bg-hover';

/** Close the settings, then do `fn` (open a page, a dialog). */
const leave = (fn: () => void) => {
  useUi.getState().set({ settingsOpen: false });
  fn();
};

export function TemplateSettings() {
  const { t } = useTranslation();
  const store = useStore();
  const templates = useStoreQuery((s) => listAllTemplates(s), [], ['pages', 'props', 'settings']);
  const create = async () => {
    const page = await createGeneralTemplate(store, t('templates.newName'));
    leave(() => {
      useUi.getState().open({ kind: 'page', pageId: page.id });
      useTemplateUi.getState().set({ settingsFor: page.id });
    });
  };
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        <button type="button" className={primary} onClick={() => void create()}>
          <Plus className="size-4" />
          {t('templates.new')}
        </button>
        <button
          type="button"
          className={secondary}
          onClick={() => leave(() => useUi.getState().open({ kind: 'templates' }))}
        >
          <LayoutTemplate className="size-4" />
          {t('settings.modules.allTemplates')}
        </button>
      </div>
      <ul className="flex flex-col">
        {templates?.length === 0 && (
          <li className="text-sm text-fg-muted">{t('templates.noneGeneral')}</li>
        )}
        {templates?.map((x) => (
          <li key={x.page.id}>
            <button
              type="button"
              className={row}
              onClick={() =>
                leave(() => useUi.getState().open({ kind: 'page', pageId: x.page.id }))
              }
            >
              <PageIcon icon={x.page.icon} fallback="🧩" />
              <span className="min-w-0 flex-1 truncate">{x.page.title || t('page.untitled')}</span>
              {x.database && <span className="text-xs text-fg-muted">{x.database.title}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PluginSettings() {
  const { t } = useTranslation();
  const plugins = usePlugins();
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={primary}
          onClick={() => leave(() => useBuilder.getState().open(null))}
        >
          <Sparkles className="size-4" />
          {t('plugins.newWithAi')}
        </button>
        <button
          type="button"
          className={secondary}
          onClick={() => leave(() => usePluginEditor.getState().set('new'))}
        >
          <FileCode2 className="size-4" />
          {t('plugins.newByHand')}
        </button>
        <button
          type="button"
          className={secondary}
          onClick={() => leave(() => useUi.getState().open({ kind: 'plugins' }))}
        >
          <Puzzle className="size-4" />
          {t('settings.modules.allPlugins')}
        </button>
      </div>
      <ul className="flex flex-col">
        {plugins?.length === 0 && (
          <li className="text-sm text-fg-muted">{t('settings.modules.noPlugins')}</li>
        )}
        {plugins?.map((p) => (
          <li key={p.pageId}>
            <button
              type="button"
              className={row}
              onClick={() => leave(() => useUi.getState().open({ kind: 'plugins' }))}
            >
              <span aria-hidden>🧩</span>
              <span className="min-w-0 flex-1 truncate">{p.manifest.name}</span>
              <span className="text-xs text-fg-muted">
                {p.enabled ? t('settings.modules.on') : t('settings.modules.off')}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
